import { ActionIcon, Button, Checkbox } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { LogoIllumi } from "@src/assets/icons";
import IconDownload from "@src/assets/icons/tabler/download.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import config from "@src/config";
import { isDesktopBrowser, openInDesktopApp } from "@src/utils/app-url";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useEffect, useRef, useState } from "react";

export const DESKTOP_INVITE_DISMISSED_KEY = "illumi:desktop-invite:dismissed";
export const DESKTOP_INVITE_ALWAYS_OPEN_KEY =
	"illumi:desktop-invite:always-open";

export const DesktopInviteBanner = memo(function DesktopInviteBanner() {
	const [isClient, setIsClient] = useState(false);
	const [launchStatus, setLaunchStatus] = useState<"idle" | "launching">(
		"idle",
	);

	const [dismissed, setDismissed] = useLocalStorage<boolean>({
		key: DESKTOP_INVITE_DISMISSED_KEY,
		defaultValue: false,
	});

	const [alwaysOpen, setAlwaysOpen] = useLocalStorage<boolean>({
		key: DESKTOP_INVITE_ALWAYS_OPEN_KEY,
		defaultValue: false,
	});

	const [rememberChoice, setRememberChoice] = useState(false);
	const hasAutoOpenedRef = useRef(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	useEffect(() => {
		if (alwaysOpen) {
			setRememberChoice(true);
		}
	}, [alwaysOpen]);

	// Auto-open on initial load if user chose "always open"
	useEffect(() => {
		if (
			isClient &&
			alwaysOpen &&
			!dismissed &&
			!hasAutoOpenedRef.current &&
			isDesktopBrowser()
		) {
			hasAutoOpenedRef.current = true;
			openInDesktopApp();
			setLaunchStatus("launching");
		}
	}, [isClient, alwaysOpen, dismissed]);

	if (!isClient || !isDesktopBrowser()) {
		return null;
	}

	const downloadUrl =
		config.desktopDownloadUrl || config.officialWebsiteUrl || undefined;

	const handleOpenDesktop = () => {
		if (rememberChoice) {
			setAlwaysOpen(true);
			setDismissed(false);
		}
		setLaunchStatus("launching");
		openInDesktopApp();
	};

	const handleStayInWeb = () => {
		setAlwaysOpen(false);
		setRememberChoice(false);
		setDismissed(true);
		setLaunchStatus("idle");
	};

	const handleDismiss = () => {
		setAlwaysOpen(false);
		setRememberChoice(false);
		setDismissed(true);
		setLaunchStatus("idle");
	};

	return (
		<AnimatePresence>
			{!dismissed && (
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: "auto", opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.15 }}
					className="shrink-0 overflow-hidden"
				>
					<section
						aria-label="Desktop app invitation"
						className={clsx(
							"flex flex-wrap items-center justify-between gap-3",
							"px-4 py-2 bg-surface-primary border-b border-border-subtle",
							"text-text-primary text-xs shrink-0 select-none",
						)}
					>
						<div className="flex items-center gap-2.5 min-w-0">
							<LogoIllumi className="w-4 h-4 shrink-0" />
							{launchStatus === "launching" ? (
								<span className="font-medium text-text-primary">
									Opening in Illumi desktop app... If nothing happens, make sure
									the app is installed.
								</span>
							) : (
								<span className="font-medium text-text-primary">
									Prefer the desktop app? Open this in Illumi for a faster,
									native experience.
								</span>
							)}
						</div>

						<div className="flex items-center gap-2.5 shrink-0 ml-auto">
							{launchStatus === "idle" && (
								<Checkbox
									label="Always open in app"
									checked={rememberChoice}
									onChange={(e) => {
										setRememberChoice(e.currentTarget.checked);
									}}
									size="xs"
									color="var(--ui-color-brand-primary)"
									radius="sm"
									classNames={{
										label: "text-xs text-text-secondary cursor-pointer",
									}}
								/>
							)}

							<Button
								onClick={handleOpenDesktop}
								size="xs"
								classNames={{
									root: clsx(
										"h-7 px-3 rounded-lg font-semibold text-xs border-none",
										"bg-brand-primary hover:bg-brand-primary-hover",
										"text-text-inverse",
									),
								}}
								title="Open in Desktop App"
							>
								{launchStatus === "launching" ? "Open Again" : "Open in App"}
							</Button>

							{downloadUrl && (
								<Button
									component="a"
									href={downloadUrl}
									target="_blank"
									rel="noreferrer"
									size="xs"
									variant="subtle"
									leftSection={<IconDownload className="w-3.5 h-3.5" />}
									classNames={{
										root: clsx(
											"h-7 px-2.5 rounded-lg text-xs border-none",
											"text-text-secondary hover:text-text-primary",
											"hover:bg-surface-hover-subtle",
										),
									}}
								>
									Download
								</Button>
							)}

							{launchStatus === "launching" && (
								<Button
									onClick={handleStayInWeb}
									size="xs"
									variant="subtle"
									classNames={{
										root: clsx(
											"h-7 px-2.5 rounded-lg text-xs border-none",
											"text-text-secondary hover:text-text-primary",
											"hover:bg-surface-hover-subtle",
										),
									}}
								>
									Stay in browser
								</Button>
							)}

							<ActionIcon
								onClick={handleDismiss}
								variant="subtle"
								size="xs"
								className={clsx(
									"text-text-secondary hover:text-text-primary",
									"hover:bg-surface-hover-subtle",
								)}
								aria-label="Dismiss desktop app invite"
								title="Dismiss"
							>
								<IconX className="w-3.5 h-3.5" />
							</ActionIcon>
						</div>
					</section>
				</motion.div>
			)}
		</AnimatePresence>
	);
});
